<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useRouter} from "vue-router";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamTask} from "@/core/interfaces/TeamTask";
import {TeamTaskStoreRequest} from "@/core/http/resources/TeamTaskResource";
import {TASK_PRIORITIES, columnLabel, initialsOf, priorityOption} from "@/team-task/taskBoard";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import TeamTaskCommentThread from "@/team-task/components/TeamTaskCommentThread.vue";
import AgendaPatientSearch, {SelectedPatient} from "@/agenda/components/AgendaPatientSearch.vue";
import {ArrowUpRight, Check, Pencil, UserRoundX} from "@lucide/vue";

const {open, task, members, currentAccessId, saving = false} = defineProps<{
    open: boolean;
    /** Null opens the creation form. */
    task: TeamTask | null;
    members: LegacyAccess[];
    /** Access point of the session, the opening suggestion for a comment. */
    currentAccessId: number | null;
    /**
     * Held by the parent, which is the only one that knows when the write
     * resolved. A flag owned here would stay raised after a failed save — the
     * dialog stays open in that case — and lock the button for good.
     */
    saving?: boolean;
}>();

const emits = defineEmits<{
    (event: 'update:open', open: boolean): void;
    (event: 'submit', payload: TeamTaskStoreRequest): void;
    (event: 'commented'): void;
}>();

/**
 * An existing task opens read-only, as the design shows it, and only turns
 * into a form on demand. Creation has nothing to read, so it starts editing.
 */
const editing = ref<boolean>(false);

const title = ref<string>('');
const description = ref<string>('');
const priority = ref<TeamTask['priority']>('normal');
const assignees = ref<number[]>([]);
const dueDate = ref<string>('');
const category = ref<string>('');
const patient = ref<SelectedPatient | null>(null);

const router = useRouter();

const isExisting = computed(() => !!task);

/** The patient file is where anything about the patient is actually done. */
const openPatient = () => {
    if (!task?.customer_id) {
        return;
    }

    emits('update:open', false);
    router.push({name: 'customer.home', params: {customerId: task.customer_id}});
};

const fillFromTask = () => {
    title.value = task?.title ?? '';
    description.value = task?.description ?? '';
    priority.value = task?.priority ?? 'normal';
    assignees.value = [...(task?.assignee_ids ?? [])];
    dueDate.value = task?.due_date ?? '';
    category.value = task?.category ?? '';
    patient.value = task?.customer
        ? {
            id: task.customer.id,
            name: `${task.customer.first_name ?? ''} ${task.customer.last_name ?? ''}`.trim() || 'Patient'
        }
        : null;
};

/**
 * Rebuilt whenever the dialog opens, never while it is open: reacting to the
 * task itself would wipe what the pharmacist is typing when the board
 * refreshes underneath.
 */
watch(() => open, isOpen => {
    if (!isOpen) {
        return;
    }

    editing.value = !task;
    fillFromTask();
}, {immediate: true});

/**
 * A save answers with the stored task, and the parent hands it back as a new
 * object: that is the signal to drop back to reading, now showing what was
 * actually saved. Watching the live prop rather than remounting keeps the
 * comment thread and its scroll position intact.
 */
watch(() => task, (next, previous) => {
    if (open && editing.value && next && previous && next !== previous && next.id === previous.id) {
        fillFromTask();
        editing.value = false;
    }
});

/** Leaving the form on an existing task falls back to reading, not closing. */
const cancel = () => {
    if (isExisting.value) {
        fillFromTask();
        editing.value = false;

        return;
    }

    emits('update:open', false);
};

const memberName = (accessPointId: number): string =>
    members.find(member => member.id === accessPointId)?.user?.name ?? 'Membre retiré';

const assignedMembers = computed(() => (task?.assignee_ids ?? []).map(id => ({
    id,
    name: memberName(id)
})));

const dueLabel = computed(() =>
    task?.due_date ? dayjs(task.due_date).locale('fr').format('dddd D MMMM YYYY') : null
);

const toggleAssignee = (memberId: number) => {
    assignees.value = assignees.value.includes(memberId)
        ? assignees.value.filter(id => id !== memberId)
        : [...assignees.value, memberId];
};

const submit = () => {
    if (!title.value.trim()) {
        return;
    }

    emits('submit', {
        title: title.value.trim(),
        description: description.value.trim() || null,
        priority: priority.value,
        assignees: assignees.value,
        due_date: dueDate.value || null,
        category: category.value.trim() || null,
        customer_id: patient.value?.id ?? null
    });
};
</script>

<template>
  <Dialog :open="open" @update:open="value => emits('update:open', value)">
    <DialogContent class="max-w-lg">
      <DialogHeader>
        <DialogTitle>
          {{ !isExisting ? 'Nouvelle tâche' : editing ? 'Modifier la tâche' : 'Détail de la tâche' }}
        </DialogTitle>
      </DialogHeader>

      <!-- Reading -->
      <div v-if="isExisting && !editing" class="space-y-4">
        <div class="flex items-start gap-2">
          <h3 class="flex-1 text-sm font-semibold leading-snug text-gray-900">{{ task!.title }}</h3>
          <span
            class="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold"
            :class="priorityOption(task!.priority).chipClass"
          >
            {{ priorityOption(task!.priority).label }}
          </span>
        </div>

        <p v-if="task!.description" class="whitespace-pre-wrap text-xs leading-relaxed text-gray-600">
          {{ task!.description }}
        </p>
        <p v-else class="text-xs italic text-gray-400">Aucune description.</p>

        <dl class="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 px-3 py-2.5">
          <div>
            <dt class="text-[10px] font-semibold text-gray-500">Statut</dt>
            <dd class="text-xs text-gray-800">{{ columnLabel(task!.status) }}</dd>
          </div>
          <div>
            <dt class="text-[10px] font-semibold text-gray-500">Échéance</dt>
            <dd class="text-xs capitalize text-gray-800">{{ dueLabel ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-[10px] font-semibold text-gray-500">Catégorie</dt>
            <dd class="text-xs text-gray-800">{{ task!.category ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-[10px] font-semibold text-gray-500">Patient</dt>
            <dd class="text-xs">
              <button
                v-if="task!.customer_id"
                class="flex items-center gap-1 font-semibold text-primary hover:underline"
                @click="openPatient"
              >
                {{ patient?.name ?? 'Patient' }}
                <ArrowUpRight class="size-3" />
              </button>
              <span v-else class="text-gray-800">—</span>
            </dd>
          </div>
        </dl>

        <div>
          <p class="mb-1.5 text-[10px] font-semibold text-gray-500">Assignée à</p>
          <div v-if="assignedMembers.length" class="flex flex-wrap gap-1.5">
            <span
              v-for="member in assignedMembers"
              :key="member.id"
              class="flex items-center gap-1.5 rounded-lg bg-primary/5 py-1 pl-1 pr-2 text-[11px] font-medium text-primary"
            >
              <span class="flex size-5 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold">
                {{ initialsOf(member.name) }}
              </span>
              {{ member.name }}
            </span>
          </div>
          <p v-else class="text-xs text-gray-500">Non assignée</p>
        </div>

        <TeamTaskCommentThread
          :task-id="task!.id"
          :members="members"
          :default-author-id="currentAccessId"
          @posted="emits('commented')"
        />
      </div>

      <!-- Writing -->
      <div v-else class="space-y-4">
        <div>
          <label class="mb-1 block text-[11px] font-semibold text-gray-500">Intitulé</label>
          <input
            v-model="title"
            placeholder="Ex. ranger la commande grossiste…"
            class="h-10 w-full rounded-lg border border-gray-200 px-3 text-xs focus:border-primary focus:outline-none"
          >
        </div>

        <div>
          <label class="mb-1 block text-[11px] font-semibold text-gray-500">Description</label>
          <textarea
            v-model="description"
            rows="3"
            placeholder="Détails, consignes, contexte…"
            class="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-xs focus:border-primary focus:outline-none"
          />
        </div>

        <div>
          <label class="mb-1.5 block text-[11px] font-semibold text-gray-500">
            Assigner à <span class="font-normal">(plusieurs possibles)</span>
          </label>
          <div class="flex flex-wrap gap-1.5">
            <button
              class="flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-medium transition"
              :class="assignees.length
                ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                : 'border-primary bg-primary/5 text-primary'"
              @click="assignees = []"
            >
              <UserRoundX class="size-3.5" />
              Non assignée (libre)
            </button>
            <button
              v-for="member in members"
              :key="member.id"
              class="flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-medium transition"
              :class="assignees.includes(member.id)
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'"
              @click="toggleAssignee(member.id)"
            >
              <Check v-if="assignees.includes(member.id)" class="size-3" />
              {{ member.user?.name ?? initialsOf(member.user?.name) }}
            </button>
          </div>
        </div>

        <div>
          <label class="mb-1.5 block text-[11px] font-semibold text-gray-500">Priorité</label>
          <div class="flex gap-2">
            <button
              v-for="option in TASK_PRIORITIES"
              :key="option.value"
              class="flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11px] font-semibold transition"
              :class="priority === option.value
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'"
              @click="priority = option.value"
            >
              <span class="size-1.5 rounded-full" :class="option.dotClass" />
              {{ option.label }}
            </button>
          </div>
        </div>

        <!-- The mockup's creation modal carries neither of these, yet the card
             displays both: they are settled here. -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="mb-1 block text-[11px] font-semibold text-gray-500">Échéance</label>
            <input
              v-model="dueDate"
              type="date"
              class="h-9 w-full rounded-lg border border-gray-200 px-2 text-xs focus:border-primary focus:outline-none"
            >
          </div>
          <div>
            <label class="mb-1 block text-[11px] font-semibold text-gray-500">Catégorie</label>
            <input
              v-model="category"
              placeholder="Ex. Commandes"
              class="h-9 w-full rounded-lg border border-gray-200 px-2 text-xs focus:border-primary focus:outline-none"
            >
          </div>
        </div>

        <div>
          <label class="mb-1 block text-[11px] font-semibold text-gray-500">
            Patient <span class="font-normal">(facultatif)</span>
          </label>
          <AgendaPatientSearch v-model="patient" />
        </div>
      </div>

      <div class="mt-2 flex gap-2">
        <template v-if="isExisting && !editing">
          <button
            class="h-10 flex-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:border-gray-300"
            @click="emits('update:open', false)"
          >
            Fermer
          </button>
          <button
            class="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary text-xs font-semibold text-white"
            @click="editing = true"
          >
            <Pencil class="size-3.5" />
            Modifier
          </button>
        </template>
        <template v-else>
          <button
            class="h-10 flex-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:border-gray-300"
            @click="cancel"
          >
            Annuler
          </button>
          <button
            class="h-10 flex-1 rounded-lg bg-primary text-xs font-semibold text-white disabled:opacity-50"
            :disabled="!title.trim() || saving"
            @click="submit"
          >
            {{ isExisting ? 'Enregistrer' : 'Créer la tâche' }}
          </button>
        </template>
      </div>
    </DialogContent>
  </Dialog>
</template>
